import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { isEmpty, set, concat } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as suggestionTodoListAction from '../Suggestions/store/Suggestion.action';
import { createOptions, checkArray, Page_Size, bomConfigData } from '../../../../Components/helper';
import { getSupplierBrandsAction } from '../Costing/store/Costing.action';
import * as todoAction from './store/SupplierToDo.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';

const customStyles = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  container: () => ({
    // none of react-select's styles are passed to <Control />
    flex: '1',
    minWidth: 200,
    marginRight: 10,
  }),
  control: () => ({
    // none of react-select's styles are passed to <Control />
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '24px',
    height: '24px',
    border: 'none',
    borderBottom: '1px solid #848484',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: 'var(--16px)',
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: '-9px 0 0 0',
    border: '1px solid #848484',
    borderRadius: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 4px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '0 4px',
  }),
};

function SupplierFilter() {
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');
  const supplierTodo = 'supplier-todo';

  const dispatch = useDispatch();
  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: {
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
      userInfo: { userId },
    },
    SystemMastersDetails: { multiSubBrandList, pcTypeList, licensorList, licenseList, likenessList },
    SupplierTodoModule: {
      sectionOptions,
      supplierFilterValues: {
        selected_Season,
        selected_Brand,
        selectedSubBrand,
        selected_Supplier,
        selected_Category1,
        selected_Category2,
        selected_Category3,
        selected_Category4,
        selectedProductType,
        selected_Status,
        selected_Section,
        sortOrder,
        sortPropertyName,
        selectedBomFields,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
      },
    },
  } = reducers;

  const [state, setState] = useState({
    seasonOptions: [],
    brandOptions: [],
    subBrandOptions: [],
    supplierOptions: [],
    sampleTypeOptions: [],
    sampleStatusOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    statusOptions: [],
    status: null,
    module: null,
    bomFields: [],
    productTypeOptions: [],
  });
  const {
    seasonOptions,
    brandOptions,
    subBrandOptions,
    supplierOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    statusOptions,
    productTypeOptions,
  } = state;

  const [isApply, setIsApply] = useState(false);
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);
  // Prevent re-entrant fetch loops
  const isFetchingCatsRef = useRef(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('supplierTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0 || parsed?.selectedLicense?.length > 0 || parsed?.selectedLikeness?.length > 0) {
        dispatch(
          todoAction.filterSortData({
            selectedLicensor: parsed.selectedLicensor || [],
            selectedLicense: parsed.selectedLicense || [],
            selectedLikeness: parsed.selectedLikeness || [],
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: parsed.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: parsed.selectedLicensor,
            licenseIds: parsed.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        console.log('Error fetching catBasedOnPTData:', e);
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  useEffect(() => {
    const isTrue = JSON.parse(localStorage.getItem('supplierTodoListFilter'));
    if (isTrue) {
      for (const keys in isTrue) {
        if (keys === 'selected_Status' || keys === 'selected_Section') {
          if (isTrue[keys]) {
            setIsApply(true);
          }
        } else if (isTrue?.[keys]?.length > 0) {
          setIsApply(true);
        }
      }
      setState((prev) => ({
        ...prev,
        status: isTrue.selected_Status
          ? {
              label: isTrue.selected_Status,
              value: isTrue.selected_Status,
            }
          : null,
        module: {
          label: isTrue.selected_Section,
          value: isTrue.selected_Section,
        },
        showOnly: {
          label: isTrue.selected_Showonly,
          value: isTrue.selected_Showonly,
        },
      }));
    }
  }, []);

  // Rehydrate saved filter selections into Redux on initial mount so dependent fetches have parent IDs
  useEffect(() => {
    const saved = localStorage.getItem('supplierTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      const allEmpty = [
        selected_Season,
        selected_Brand,
        selectedSubBrand,
        selected_Supplier,
        selectedProductType,
        selected_Category1,
        selected_Category2,
        selected_Category3,
        selected_Category4,
        selected_Status,
        selected_Section,
      ].every((v) => (Array.isArray(v) ? v.length === 0 : !v));
      if (allEmpty) {
        dispatch(todoAction.filterSortData(parsed));
      }
    } catch (e) {
      // ignore corrupt saved state
    }
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(pcTypeList) && pcTypeList.length > 0) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') }));
    } else {
      setState((prev) => ({ ...prev, productTypeOptions: [] }));
    }
  }, [pcTypeList]);

  // Helpers for cascading filtering (same as reference file)
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);
  const selectedPTIds = getIds(selectedProductType);
  const selectedCat1Ids = getIds(selected_Category1);
  const selectedCat2Ids = getIds(selected_Category2);
  const selectedCat3Ids = getIds(selected_Category3);

  // Stable keys for effects to avoid re-runs on reference-only changes
  const selCat1Key = useMemo(() => (selectedCat1Ids.length ? [...selectedCat1Ids].sort().join(',') : ''), [selectedCat1Ids]);
  const selCat2Key = useMemo(() => (selectedCat2Ids.length ? [...selectedCat2Ids].sort().join(',') : ''), [selectedCat2Ids]);
  const selCat3Key = useMemo(() => (selectedCat3Ids.length ? [...selectedCat3Ids].sort().join(',') : ''), [selectedCat3Ids]);
  const selPTKey = useMemo(() => (selectedPTIds.length ? [...selectedPTIds].sort().join(',') : ''), [selectedPTIds]);

  // Cache last fetched parent keys to prevent duplicate API calls
  const lastCat3FetchKeyRef = useRef(''); // key: c1|c2|pt
  const lastCat4FetchKeyRef = useRef(''); // key: c1|c2|c3Key|pt

  // Client-side filtering using useMemo (same approach as reference file)
  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
      return usedCat1?.some((id) => selectedCat1Ids.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId) && selectedCat3Ids.includes(opt?.categoryThreeId),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  // Helpers to avoid redundant setState on options
  const optionIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((o) => String(o?._id ?? o?.value))
          .filter(Boolean)
          .sort()
      : [];
  const areOptionIdsEqual = (a, b) => {
    const aa = optionIds(a);
    const bb = optionIds(b);
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) if (aa[i] !== bb[i]) return false;
    return true;
  };

  // Fetch all categories (same as reference file approach)
  const fetchCategories = useCallback(async () => {
    // Always hydrate Category 1 and 2 base lists
    for (const cat of [1, 2]) {
      const res = await dispatch(
        suggestionTodoListAction.fetchCategoryAction({
          categoryType: cat,
          loggedInUserId: userId,
        }),
      );
      const list = Array.isArray(res?.data) ? res.data : res;
      setState((prevState) => ({ ...prevState, [`category${cat}Options`]: createOptions(list, 'name', '_id') }));
    }

    // Hydrate Category 3 if Cat1+Cat2 are already selected (support multi-select)
    if (Array.isArray(selected_Category1) && selected_Category1.length && Array.isArray(selected_Category2) && selected_Category2.length) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const calls = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          const params = {
            categoryType: 3,
            loggedInUserId: userId,
            categoryOneId: c1,
            categoryTwoId: c2,
            isActive: 1,
          };
          if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length) {
            params.productTypeIds = selectedProductType;
          }
          calls.push(dispatch(suggestionTodoListAction.fetchCategoryAction(params)));
        });
      });
      const results = await Promise.all(calls);
      const merged = [];
      const seen = new Set();
      results.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen.has(key)) {
              seen.add(key);
              merged.push(item);
            }
          });
        }
      });
      {
        const next = createOptions(merged, 'name', '_id');
        if (!areOptionIdsEqual(state.category3Options, next)) {
          setState((prevState) => ({ ...prevState, category3Options: next }));
        }
      }
    }

    // Hydrate Category 4 if Cat1+Cat2+Cat3 are already selected (support multi Cat1/Cat2/Cat3)
    if (
      Array.isArray(selected_Category1) &&
      selected_Category1.length &&
      Array.isArray(selected_Category2) &&
      selected_Category2.length &&
      Array.isArray(selected_Category3) &&
      selected_Category3.length
    ) {
      const c1s = selected_Category1;
      const c2s = selected_Category2;
      const c3s = selected_Category3;
      const calls = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          c3s.forEach((id) => {
            const params = {
              categoryType: 4,
              loggedInUserId: userId,
              categoryOneId: c1,
              categoryTwoId: c2,
              categoryThreeId: id,
              isActive: 1,
            };
            if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length) {
              params.productTypeIds = selectedProductType;
            }
            calls.push(dispatch(suggestionTodoListAction.fetchCategoryAction(params)));
          });
        });
      });
      const results = await Promise.all(calls);
      const merged = [];
      const seen = new Set();
      results.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen.has(key)) {
              seen.add(key);
              merged.push(item);
            }
          });
        }
      });
      {
        const next = createOptions(merged, 'name', '_id');
        if (!areOptionIdsEqual(state.category4Options, next)) {
          setState((prevState) => ({ ...prevState, category4Options: next }));
        }
      }
    }
  }, [dispatch, userId]);

  // Fetch subbrands when brand selection changes
  useEffect(() => {
    const brandId = selected_Brand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selected_Brand]);

  // Map subbrand list from reducer to options
  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  const handleSelectChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);

    // When Product Type changes and cascading is ON, clear all downstream category selections
    if (name === 'selectedProductType' && catBasedOnPTData) {
      obj.selected_Category1 = [];
      obj.selected_Category2 = [];
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }

    if (
      ['selected_Category1', 'selected_Category2'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category3 = [];
      obj.selected_Category4 = [];
    }
    // Also reset Category 2 when Category 1 changes, and clear downstream options/caches
    if (name === 'selected_Category1') {
      obj.selected_Category2 = [];
      // Clear Cat3/Cat4 options to avoid stale UI and extra fetches
      setState((prev) => ({ ...prev, category3Options: [], category4Options: [] }));
      // Reset fetch caches so next valid parent set will fetch once
      lastCat3FetchKeyRef.current = '';
      lastCat4FetchKeyRef.current = '';
    }
    if (
      ['selected_Category3'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selected_Category4 = [];
    }
    // Reset subBrand when brand changes and clear subBrand options until new ones load
    if (
      ['selected_Brand'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
    await dispatch(todoAction.filterSortData(obj));
  };

  const licensorOptions = useMemo(() => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : []), [licensorList]);
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : []), [licenseList]);
  const likenessOptions = useMemo(() => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : []), [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(todoAction.filterSortData(newData));
  };

  const clearAll = async () => {
    setState((prev) => ({ ...prev, showOnly: null, status: null, module: null }));
    const paramsObj = {
      seasons: [],
      year: [],
      brand: [],
      subBrand: [],
      supplier: [],
      colour: [],
      status: null,
      productTypeId: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      module: null,
      actionType: null,
      licensorId: [],
      licenseId: [],
      likenessId: [],
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
      type: supplierTodo,
      bomFields: [],
    };
    const sortinObj = {
      selected_Season: [],
      selectedSuggestion_Year: [],
      selected_Brand: [],
      selectedSubBrand: [],
      selected_Supplier: [],
      selectedSuggestion_ActionType: '',
      selected_Status: '',
      selectedProductType: [],
      selected_Category1: [],
      selected_Category2: [],
      selected_Category3: [],
      selected_Category4: [],
      selected_Section: null,
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
      selectedBomFields: [],
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
    };
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    const res = await dispatch(todoAction.getSupplierTodoList(paramsObj));
    localStorage.removeItem('supplierTodoListFilter');
    setIsApply(false);
    if (res) await dispatch(todoAction.filterSortData(sortinObj));
  };

  const applyFilter = async () => {
    setIsApply(true);
    const paramsObj = {
      seasons: selected_Season,
      brand: selected_Brand,
      subBrand: selectedSubBrand,
      supplier: selected_Supplier,
      status: selected_Status,
      category1: selected_Category1,
      category2: selected_Category2,
      category3: selected_Category3,
      category4: selected_Category4,
      module: selected_Section,
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
      type: supplierTodo,
      bomFields: selectedBomFields,
      productTypeId: Array.isArray(selectedProductType) ? selectedProductType : [],
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
    };

    const paramsObj1 = {
      selected_Season,
      selected_Brand,
      selectedSubBrand,
      selected_Supplier,
      selected_Category1,
      selected_Category2,
      selected_Category3,
      selected_Category4,
      selected_Section,
      selected_Status,
      selectedProductType: Array.isArray(selectedProductType) ? selectedProductType : [],
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('supplierTodoListFilter', JSON.stringify(paramsObj1));
    await dispatch(todoAction.getSupplierTodoList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
    };
    await dispatch(todoAction.filterSortData(obj));
  };

  // all useEffect
  // call all api's for filter section to fill the dropdown list
  useEffect(() => {
    const callFn = async () => {
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrandsAction(userId))
        : await dispatch(
            suggestionTodoListAction.fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );
      // Fetch all categories (client-side filtering will handle the rest)
      await fetchCategories();

      const sesonres = await dispatch(suggestionTodoListAction.getSeasonsAction({ loggedInUserId: userId }));

      if (!isSupplierLoggedIn) {
        let supp_list = [];

        if (isCompanyUserLoggedIn) {
          supp_list = await dispatch(suggestionTodoListAction.fetchUserSuppliersAction());

          supp_list = supp_list?.suppliers;
          supp_list = supp_list?.length
            ? supp_list?.map((ele) => {
                if (ele?.companies?.length) {
                  ele.name = ele.companies[0].supplierName;
                }
                return ele;
              })
            : [];
        } else {
          supp_list = await dispatch(
            suggestionTodoListAction.getSuppliers({
              loggedInUserId: userId,
            }),
          );
        }
        if (checkArray(supp_list)) {
          const supplier_Options = createOptions(supp_list, 'name', '_id');
          setState((prevState) => ({
            ...prevState,
            supplierOptions: supplier_Options,
          }));
        }
      }

      const mainSample = await dispatch(
        suggestionTodoListAction.getMainSamplesAction({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const otherSample = await dispatch(
        suggestionTodoListAction.getOtherSamplesAction({
          loggedInUserId: userId,
          isActive: true,
        }),
      );

      if (checkArray(mainSample) || checkArray(otherSample)) {
        const mergeSamples = concat(mainSample, otherSample);
        const sampleType_Options = createOptions(mergeSamples, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          sampleTypeOptions: sampleType_Options,
        }));
      }

      const statusArr = await createOptions(['Completed', 'Pending']);

      if (statusArr) {
        setState((prevState) => ({
          ...prevState,
          statusOptions: statusArr,
        }));
      }
      if (checkArray(sesonres)) {
        const season_Options = createOptions(sesonres, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          seasonOptions: season_Options,
        }));
      }
      if (checkArray(BrandRes)) {
        const brand_Options = createOptions(BrandRes, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          brandOptions: brand_Options,
        }));
      }
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId, catBasedOnPTData, selectedProductType, fetchCategories]); // eslint-disable-line

  useEffect(() => {
    const callFn = async () => {
      if (isFetchingCatsRef.current) return;
      isFetchingCatsRef.current = true;
      try {
        const c1s = Array.isArray(selected_Category1) ? selected_Category1 : [selected_Category1];
        const c2s = Array.isArray(selected_Category2) ? selected_Category2 : [selected_Category2];
        const c3s = Array.isArray(selected_Category3) ? selected_Category3 : [selected_Category3];

        if (checkArray(selected_Category1) && checkArray(selected_Category2) && checkArray(selected_Category3) && !isEmpty(userId)) {
          // Multi Cat1/Cat2/Cat3 -> fetch Cat4 for every combination and merge
          const cacheKey = `${[...c1s].sort().join(',')}|${[...c2s].sort().join(',')}|${[...c3s].sort().join(',')}|${catBasedOnPTData ? selPTKey : ''}`;
          if (lastCat4FetchKeyRef.current === cacheKey && Array.isArray(state.category4Options) && state.category4Options.length) {
            // skip duplicate
          } else {
            const calls = [];
            c1s.forEach((c1) => {
              c2s.forEach((c2) => {
                c3s.forEach((id) => {
                  const params = {
                    categoryType: 4,
                    loggedInUserId: userId,
                    categoryOneId: c1,
                    categoryTwoId: c2,
                    categoryThreeId: id,
                    isActive: 1,
                  };
                  if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length) {
                    params.productTypeIds = selectedProductType;
                  }
                  calls.push(dispatch(suggestionTodoListAction.fetchCategoryAction(params)));
                });
              });
            });
            const results = await Promise.all(calls);
            const merged = [];
            const seen = new Set();
            results.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen.has(key)) {
                    seen.add(key);
                    merged.push(item);
                  }
                });
              }
            });
            {
              const next = createOptions(merged, 'name', '_id');
              if (!areOptionIdsEqual(state.category4Options, next)) {
                setState((prevState) => ({ ...prevState, category4Options: next }));
              }
            }
            lastCat4FetchKeyRef.current = cacheKey;
          }

          // Also hydrate Category 3 if empty
          if (!Array.isArray(category3Options) || category3Options.length === 0) {
            const calls3 = [];
            c1s.forEach((c1id) => {
              c2s.forEach((c2id) => {
                const params = {
                  categoryType: 3,
                  loggedInUserId: userId,
                  categoryOneId: c1id,
                  categoryTwoId: c2id,
                  isActive: 1,
                };
                if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length) {
                  params.productTypeIds = selectedProductType;
                }
                calls3.push(dispatch(suggestionTodoListAction.fetchCategoryAction(params)));
              });
            });
            const results3 = await Promise.all(calls3);
            const merged3 = [];
            const seen3 = new Set();
            results3.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen3.has(key)) {
                    seen3.add(key);
                    merged3.push(item);
                  }
                });
              }
            });
            {
              const next3 = createOptions(merged3, 'name', '_id');
              if (!areOptionIdsEqual(state.category3Options, next3)) {
                setState((prevState) => ({ ...prevState, category3Options: next3 }));
              }
            }
          }
        } else if (checkArray(selected_Category1) && checkArray(selected_Category2) && !isEmpty(userId)) {
          // Multi Cat1/Cat2 -> fetch Cat3 combinations and merge
          const cacheKey = `${[...c1s].sort().join(',')}|${[...c2s].sort().join(',')}|${catBasedOnPTData ? selPTKey : ''}`;
          if (lastCat3FetchKeyRef.current === cacheKey && Array.isArray(state.category3Options) && state.category3Options.length) {
            // skip duplicate
          } else {
            const calls = [];
            c1s.forEach((c1id) => {
              c2s.forEach((c2id) => {
                const params = {
                  categoryType: 3,
                  loggedInUserId: userId,
                  categoryOneId: c1id,
                  categoryTwoId: c2id,
                  isActive: 1,
                };
                if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length) {
                  params.productTypeIds = selectedProductType;
                }
                calls.push(dispatch(suggestionTodoListAction.fetchCategoryAction(params)));
              });
            });
            const results = await Promise.all(calls);
            const merged = [];
            const seen = new Set();
            results.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen.has(key)) {
                    seen.add(key);
                    merged.push(item);
                  }
                });
              }
            });
            {
              const next = createOptions(merged, 'name', '_id');
              if (!areOptionIdsEqual(state.category3Options, next)) {
                setState((prevState) => ({ ...prevState, category3Options: next }));
              }
            }
            lastCat3FetchKeyRef.current = cacheKey;
          }
        }
      } finally {
        isFetchingCatsRef.current = false;
      }
    };
    callFn();
  }, [selCat1Key, selCat2Key, selCat3Key, dispatch, userId, catBasedOnPTData, selPTKey]);
  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('TAKING_DATES.SUGGESTION_FILTER')}
    >
      <OverlayTrigger
        placement="bottom"
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="brand"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions?.filter((val) => !isEmpty(selected_Brand) && selected_Brand?.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Brand',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {subBrandAccess && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="subBrand"
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions?.filter((val) => !isEmpty(selectedSubBrand) && selectedSubBrand?.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selectedSubBrand',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {catBasedOnPTData && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="productType"
                          options={productTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={productTypeOptions.filter((opt) => Array.isArray(selectedProductType) && selectedProductType.includes(opt.value))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                          onChange={(selected) =>
                            handleSelectChange(Array.isArray(selected) ? selected.map((opt) => opt?.value).filter(Boolean) : [], 'selectedProductType')
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category1Filtered : category1Options)?.filter(
                          (val) => !isEmpty(selected_Category1) && selected_Category1?.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category1',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category2Filtered : category2Options)?.filter(
                          (val) => !isEmpty(selected_Category2) && selected_Category2?.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category2',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category3Filtered : category3Options)?.filter(
                          (val) => !isEmpty(selected_Category3) && selected_Category3?.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER'),
                        }}
                        labelledBy={t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category3',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category4Filtered : category4Options)?.filter(
                          (val) => !isEmpty(selected_Category4) && selected_Category4?.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Category4',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions?.filter((val) => !isEmpty(selected_Season) && selected_Season?.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selected_Season',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <Select
                        isClearable="true"
                        classNamePrefix="common-select"
                        name="module"
                        options={sectionOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={state?.module}
                        onChange={(selected) => {
                          setState((prev) => ({ ...prev, module: selected }));
                          handleSelectChange(selected ? selected.value : null, 'selected_Section');
                        }}
                        styles={customStyles}
                        placeholder={t('SUPPLIER_SUGGESTION_FORM.SELECT_MODULE')}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <Select
                        isClearable="true"
                        classNamePrefix="common-select"
                        name="status"
                        options={statusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        getOptionLabel={(option) => `${option.label}`}
                        getOptionValue={(option) => `${option.value}`}
                        value={state?.status}
                        onChange={(selected) => {
                          setState((prev) => ({ ...prev, status: selected }));

                          handleSelectChange(selected ? selected.value : null, 'selected_Status');
                        }}
                        styles={customStyles}
                        placeholder={t('PROGRESS_INDICATOR.SELECT_STATUS')}
                      />
                    </div>
                  </FormGroup>
                  {!isSupplierLoggedIn && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions?.filter((val) => !isEmpty(selected_Supplier) && selected_Supplier?.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selected_Supplier',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="bomFields"
                        options={bomConfigData}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={bomConfigData?.filter((val) => !isEmpty(selectedBomFields) && selectedBomFields?.includes(val.value))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val.value),
                            'selectedBomFields',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicensor',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicense',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLikeness',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default SupplierFilter;
